import {
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentReturnedOutlinedIcon from '@mui/icons-material/AssignmentReturnedOutlined';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import { showLabel } from '@/utils/helper';
import { OnoffType } from '@/api/enum/common.enum';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { DefaultValues, useForm } from 'react-hook-form';
import { DataGrid } from '../BodyListSummaryPage';
import {
	MeditationPageDTO,
	UnitSeriesPublishDTO,
} from '@/api/DTO/exercise/meditation.interface';
import {
	useGetBodyPublishingListQuery,
	useSetBodyStatusMutation,
} from '@/api/exercise/bodyApiSlice';

interface Props {}

export default function SetBodyStatusDialog(props: Props) {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(false);
	const [setBodyStatus] = useSetBodyStatusMutation();

	const onoffList = [
		{
			id: OnoffType.off,
			name: t('information.off'),
		},
		{
			id: OnoffType.on,
			name: t('information.on'),
		},
	];

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetBodyPublishingListQuery(null, {
			refetchOnMountOrArgChange: true,
			skip: !open,
		});

	const defaultValues: DefaultValues<any> = {};

	const methods = useForm<any>({
		defaultValues,
	});

	const onSubmit = async () => {
		const data: UnitSeriesPublishDTO[] = Object.entries(
			methods.getValues()
		).map(([key, value]) => ({
			seriesId: key,
			published: value == 1 ? true : false,
		}));
		const response = await setBodyStatus({ seriesPublishDTOList: data });
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateExerciseStatusSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	const handleClose = () => {
		setOpen(false);
	};

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('meditation.seriesTitleZh'),
			flex: 25,
		},
		{
			field: 'singleCount',
			headerName: t('meditation.exerciseCount'),
			flex: 25,
		},
		{
			field: 'status',
			headerName: t('meditation.seriesStatus'),
			flex: 10,
			renderCell: (params: any) => (
				<Chip
					color={
						params.row.published == OnoffType.on ? 'chipPrimary' : 'default'
					}
					label={showLabel(params.row.published, onoffList)}
				/>
			),
		},
		{
			field: 'changePublished',
			headerName: t('meditation.onoff'),
			flex: 25,
			renderCell: (params: any) => (
				<AppRadioGroup
					control={methods.control}
					name={params.row.seriesId}
					row
					options={onoffList}
				/>
			),
		},
	];

	const transformResponse = useCallback((response?: MeditationPageDTO[]) => {
		if (!response) return;
		const newResponse: DataGrid[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				id: item.seriesId,
			});

			methods.setValue(
				item.seriesId,
				item.published ? OnoffType.on : OnoffType.off
			);
		});

		return newResponse;
	}, []);

	const formContent = (
		<>
			<Button
				variant='contained'
				style={{ alignItems: 'center' }}
				endIcon={<AssignmentReturnedOutlinedIcon />}
				onClick={() => setOpen(true)}
			>
				{t('meditation.onoff')}
			</Button>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('meditation.onoff')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<Grid container alignItems='stretch' columnSpacing={3} marginTop={3}>
						<Grid item xs display='flex'>
							<LocalDataGrid
								rows={transformResponse(propertySummary?.data) ?? []}
								columns={columns}
								rowCount={0}
								getRowId={(row) => row.seriesId}
								pagination={false}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button type='submit' variant='contained' onClick={() => onSubmit()}>
						{t('button.save')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
